/**
 * Member detail shell (v2, DNKADM-0059) for loading one member, rendering the
 * header, owning the outer tab state, and wiring shared dialogs/reload flow.
 *
 * Split module map:
 *  - Timeline tab (DNKADM-0429): `MemberTimelineTab.tsx`
 *  - Membership/Credits tab: `LedgerTab.tsx`
 *  - Membership summary/manage controls: `MembershipSummaryBar.tsx`
 *  - Header photo control: `MemberPhotoAvatar.tsx`
 *  - Coach header picker: `CoachPickerDialog.tsx`
 *  - JSX-free shared helpers: `memberDetailShared.ts`
 *
 * `contactId` is the external contact-system id — DATA, NEVER surfaced as "GHL …" (§4.0).
 * After a credit adjust, the balance cards refetch (a bumped `reloadToken`) so the running
 * balance is server-truthful — balances are never fabricated client-side.
 */
import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, Chip, Menu, MenuItem, Paper, Skeleton, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  getMember,
  type CreditAdjustBatchResult,
  type CreditBalance,
  type MemberDetail as MemberDetailData,
  type MemberErasureStatus,
  type MemberHomeLocation,
  type MemberCoach,
  type ResendVerificationOutcome,
} from '@/api/members';
import { IconAction } from '@/components/IconAction';
import { MemberNameText } from '@/components/MemberNameText';
import { useCurrentOperator, useStaffTier } from '@/auth/authHooks';
import { useMoneyPermissions } from '@/auth/moneyPermissions';
import { useListUrlState } from '@/components/list/useListUrlState';
import { useToast } from '@/components/useToast';
import { formatPhoneForDisplay } from '@/components/phone/phoneFormat';
import { MemberReservationsPanel } from '@/features/reservations/MemberReservationsPanel';
import { avatarFor, chipSx, Icon, inkAlpha, MONO, resolveTokens } from '@/theme';
import { AdjustCreditsDialog } from './CreditBatchDialog';
import { CoachPickerDialog } from './CoachPickerDialog';
import { DeleteMemberDialog } from './DeleteMemberDialog';
import { HomeLocationPickerDialog } from './HomeLocationControl';
import { MemberActivitySection } from './MemberActivitySection';
import { MemberGuestsTab } from './MemberGuestsTab';
import { MemberPaymentMethodsTab } from './MemberPaymentMethodsTab';
import { MemberPaymentsTab } from './MemberPaymentsTab';
import { MemberPhotoAvatar } from './MemberPhotoAvatar';
import { MemberProfileTab } from './MemberProfileTab';
import { MemberTimelineTab } from './MemberTimelineTab';
import { TIMELINE_COPY } from './memberTimelineCopy';
import { ResendVerificationDialog } from './ResendVerificationDialog';
import { StaffMemberProfilePanel } from './StaffMemberProfilePanel';
import { LedgerTab } from './LedgerTab';
import { coachAssignmentState, coachDisplayName, useCoachChange } from './coachChange';
import { homeName, useHomeLocationChange } from './homeLocationChange';
import {
  PLACEHOLDER,
  accessEndingLabel,
  erasureOutcomeMessage,
  formatJoined,
  memberDetailPanelA11yProps,
  memberDetailTabA11yProps,
  membersName,
  paymentFailedLabel,
  statusChip,
  tierChip,
  type LoadState,
} from './memberDetailShared';
import { actionErrorMessage } from '@/api/screenError';
import { MEMBERS_FORBIDDEN_MESSAGE, MEMBERS_LOAD_FAILED_MESSAGE } from './membersError';
import {
  MEMBER_DETAIL_TAB_PARAM,
  MEMBER_DETAIL_URL_DEFAULTS,
  parseMemberDetailTab,
} from './memberDetailView';
import { ScreenError } from '@/components/ScreenError';
import { useCalendarZone } from '@/location/useCalendarZone';

export function MemberDetail() {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const { contactId } = useParams<{ contactId: string }>();
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  // Bumped after a successful credit adjust to refetch the detail (the bucket cards)
  // so balances are server-truthful; the same token flows to the ledger table.
  const [reloadToken, setReloadToken] = useState(0);
  const loadedContactIdRef = useRef<string | null>(null);
  // DNKADM-0436 V-17: a non-initial reload that fails keeps the last data on screen but must
  // say so. Cleared when a reload starts, and on any success.
  const [refreshError, setRefreshError] = useState<unknown>(null);

  useEffect(() => {
    if (!contactId) {
      setState({ status: 'error', error: new Error('Missing member id') });
      return;
    }
    let cancelled = false;
    const isInitialLoad = loadedContactIdRef.current !== contactId;
    setRefreshError(null);
    if (isInitialLoad) setState({ status: 'loading' });
    getMember(contactId)
      .then((data) => {
        if (!cancelled) {
          loadedContactIdRef.current = contactId;
          setState({ status: 'loaded', data });
        }
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (isInitialLoad) setState({ status: 'error', error });
        else setRefreshError(error ?? new Error('Member refresh failed'));
      });
    return () => {
      cancelled = true;
    };
  }, [contactId, reloadToken]);

  return (
    <Box>
      <Button
        component={RouterLink}
        to="/members"
        startIcon={<Icon name="arrow_back" />}
        sx={{ color: t.text2, mb: 1.75 }}
      >
        All members
      </Button>

      {state.status === 'loading' && <DetailSkeleton />}
      {state.status === 'error' && (
        <ScreenError
          variant="panel"
          error={state.error}
          what="the member"
          onRetry={() => setReloadToken((n) => n + 1)}
          forbiddenMessage={MEMBERS_FORBIDDEN_MESSAGE}
          retryMessage={MEMBERS_LOAD_FAILED_MESSAGE}
          preferServerMessage
          sx={{ mt: 1 }}
        />
      )}
      {state.status === 'loaded' && refreshError !== null && (
        <Alert
          severity="error"
          role="alert"
          sx={{ mb: 1.5 }}
          action={
            <Button color="inherit" size="small" onClick={() => setReloadToken((n) => n + 1)}>
              Retry
            </Button>
          }
        >
          Could not refresh.{' '}
          {actionErrorMessage(refreshError, MEMBERS_LOAD_FAILED_MESSAGE)}{' '}
          The details below may be out of date.
        </Alert>
      )}
      {state.status === 'loaded' && (
        <DetailBody
          data={state.data}
          contactId={contactId ?? state.data.id ?? ''}
          reloadToken={reloadToken}
          onReload={() => setReloadToken((t) => t + 1)}
        />
      )}
    </Box>
  );
}

/** Brief skeleton header shown while opening a member. */
function DetailSkeleton() {
  return (
    <Paper elevation={1} sx={{ p: '22px 24px' }} role="status" aria-label="Loading member">
      <Stack direction="row" sx={{ alignItems: 'flex-start', gap: 2.25 }}>
        <Skeleton variant="circular" width={64} height={64} />
        <Box sx={{ flex: 1 }}>
          <Skeleton width={220} height={26} />
          <Skeleton width={320} height={14} sx={{ mt: 1.25 }} />
        </Box>
        <Stack direction="row" sx={{ gap: 1.25 }}>
          <Skeleton variant="rounded" width={140} height={38} />
          <Skeleton variant="rounded" width={170} height={38} />
        </Stack>
      </Stack>
    </Paper>
  );
}

function DetailBody({
  data,
  contactId,
  reloadToken,
  onReload,
}: {
  data: MemberDetailData;
  contactId: string;
  reloadToken: number;
  onReload: () => void;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const calendarZone = useCalendarZone();
  // Balances are stateful so a successful adjust updates the shown figure in place.
  const [balances, setBalances] = useState<CreditBalance[] | null>(data.creditBalances);
  useEffect(() => {
    setBalances(data.creditBalances);
  }, [data.creditBalances]);

  // Plan/status display values are seeded from the detail; the editor (which loads the
  // plan id separately) updates these in place from its write response.
  const [membershipDisplay, setMembershipDisplay] = useState<{
    tier: string | null;
    status: string | null;
  }>({ tier: data.membershipTier, status: data.membershipStatus });
  useEffect(() => {
    setMembershipDisplay({ tier: data.membershipTier, status: data.membershipStatus });
  }, [data.membershipTier, data.membershipStatus]);

  // The effective home location (DNKADM-0239) is seeded from the detail read; a
  // successful set reflects the server's authoritative view in place.
  const [homeLocation, setHomeLocation] = useState<MemberHomeLocation | null>(data.homeLocation);
  useEffect(() => {
    setHomeLocation(data.homeLocation);
  }, [data.homeLocation]);

  // The coach is embedded on the member read (DNKAPI-0460), so the header has
  // useful context immediately and never performs a separate initial coach read.
  const [coach, setCoach] = useState<MemberCoach>(data.coach);
  useEffect(() => {
    setCoach(data.coach);
  }, [data.coach]);

  // DNKADM-0375: STAFF is front of house. DNKADM-0465 (DNKAPI-0675): every operator,
  // STAFF included, gets all eight tabs. Inside them, card Remove/Replace and guest
  // Block/Unblock follow the money seam (`canRemoveCards` / `canBlockGuests`) and order
  // Refund/Void stays OWNER/ADMIN. `fullAccess` still keeps the profile editor, the photo,
  // home location and coach, membership changes and Manage credits (reduce / expiry are
  // ADMIN on the server) to a non-STAFF session. Adjust credits follows the money seam
  // (`useMoneyPermissions`, DNKAPI-0670).
  const fullAccess = !useStaffTier();
  const { canAdjustCredits } = useMoneyPermissions();
  // Membership/Credits is the first tab and the default on open (DNKADM-0235,
  // superseding the DNKADM-0155 Reservations-default for this surface — tab
  // order/default only), so the default index is 0 = Membership/Credits;
  // Payment methods is 1 (DNKADM-0252 — beside the other money surface) and
  // Reservations is 2.
  //
  // The indices are STABLE across both variants — every `Tab` below carries an
  // explicit `value`, so rendering a subset cannot renumber the panels (MUI would
  // otherwise assign values by child position) and the a11y ids stay paired.
  //
  // DNKADM-0430 P1-D: the open tab lives in the URL (`?tab=`, see `memberDetailView.ts`),
  // so a link or a reload reopens it. A tab change REPLACES the history entry, so Back
  // leaves the member rather than stepping through tabs.
  const tabUrl = useListUrlState(MEMBER_DETAIL_URL_DEFAULTS);
  const tab = parseMemberDetailTab(tabUrl.values.tab);
  const setTabUrl = tabUrl.set;
  const setTab = (next: number) => setTabUrl({ tab: MEMBER_DETAIL_TAB_PARAM[next] ?? null });
  const [membershipOpen, setMembershipOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);

  // The header overflow menu carries actions with DIFFERENT permissions, so the gate
  // sits on the ITEM, never on the menu (DNKADM-0247). Today it holds three:
  //  - "Set/Change home location" — available to EVERY operator who can view the member
  //    (this screen is not behind `RequireAdmin`), as it was when it lived on the tab card.
  //  - "Delete member" (GDPR erasure — DNKADM-0203) — gated on `operator.isAdmin` (the
  //    same gate as `RequireAdmin`); the /v2/admin erasure endpoint is the real authority.
  // Anything added here MUST carry its own gate — the menu itself grants nothing.
  const operator = useCurrentOperator();
  const isAdmin = operator?.isAdmin === true;
  const navigate = useNavigate();
  const showToast = useToast();
  const [actionsAnchor, setActionsAnchor] = useState<HTMLElement | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  // DNKADM-0444: "Resend verification email" is open to STAFF and above (DNKAPI-0650), and
  // only offered on an EXPLICIT `emailVerified === false` — null (unknown) never reads as
  // unconfirmed, and true has nothing to resend.
  const [resendOpen, setResendOpen] = useState(false);
  const showResend = data.emailVerified === false;
  // The one home-location write path (picker state + `setMemberHomeLocation` + toast) —
  // owned by the hook, never re-implemented here.
  const homeChange = useHomeLocationChange({ contactId, onChanged: setHomeLocation });
  const coachChange = useCoachChange({ contactId, onChanged: setCoach });
  const isHomeSet = homeLocation !== null;
  const coachState = coachAssignmentState(coach);
  const isCoachAssigned = coachState !== 'unassigned';
  const coachText = coachDisplayName(coach);

  function openActions(e: MouseEvent<HTMLElement>) {
    setActionsAnchor(e.currentTarget);
  }
  function closeActions() {
    setActionsAnchor(null);
  }
  function openDelete() {
    closeActions();
    setDeleteOpen(true);
  }
  function openResend() {
    closeActions();
    setResendOpen(true);
  }
  function handleResendDone(outcome: ResendVerificationOutcome) {
    setResendOpen(false);
    switch (outcome) {
      case 'SENT':
        showToast(`Verification email sent to ${data.email ?? 'the member'}.`);
        break;
      case 'ALREADY_VERIFIED':
        showToast("This member's email is already verified.");
        // The detail was stale: refetch so the menu item disappears.
        onReload();
        break;
      case 'NO_ACCOUNT':
        showToast("This member hasn't set up a sign-in yet.");
        break;
    }
  }
  function openHomeLocation() {
    closeActions();
    homeChange.setOpen(true);
  }
  function openCoach() {
    closeActions();
    coachChange.setOpen(true);
  }
  function handleErased(status: MemberErasureStatus) {
    setDeleteOpen(false);
    showToast(erasureOutcomeMessage(status));
    navigate('/members');
  }

  function openAdjust() {
    setTab(0);
    setAdjustOpen(true);
  }
  function openMembership() {
    setTab(0);
    setMembershipOpen(true);
  }

  function handleAdjusted(result: CreditAdjustBatchResult) {
    // Update the shown balances in-place from the batch results (keyed by creditBucketId).
    setBalances((prev) => {
      if (!prev) return prev;
      return prev.map((b) => {
        const hit = result.results.find((r) => r.creditBucketId === b.creditBucketId);
        return hit !== undefined ? { ...b, balance: hit.balance } : b;
      });
    });
    onReload();
  }

  const name = membersName(data);
  // DNKADM-0487: the billing contact for the bank's card check — the member's own stored
  // name and email (never the display fallback). Read only at tokenize time, so no memo.
  const cardBuyer = { name: data.name, email: data.email };
  const av = avatarFor(data.id ?? name, theme.palette.mode);
  const tier =
    membershipDisplay.tier !== null ? tierChip(membershipDisplay.tier, theme.palette.mode) : null;
  // DNKAPI-0600: a canceled-but-paid member reads "Active until {last day}" in the Active
  // chip's colors, rather than a plain "Active" that hides the end date.
  const statusBase =
    membershipDisplay.status !== null
      ? statusChip(membershipDisplay.status, theme.palette.mode)
      : null;
  const accessEnding = accessEndingLabel(membershipDisplay.status, data);
  const status =
    statusBase !== null && accessEnding !== null ? { ...statusBase, label: accessEnding } : statusBase;
  // DNKADM-0486 (DNKAPI-0704): a failed membership payment no card update or later success
  // has cleared. Shown whatever the tenant's block setting is; the cancelled red marks it.
  const paymentFailed = data.paymentIssue
    ? {
        ...statusChip('cancelled', theme.palette.mode),
        label: paymentFailedLabel(data.paymentIssueSince, calendarZone),
      }
    : null;

  return (
    <>
      <Paper elevation={1} sx={{ p: '22px 24px' }}>
        <Stack direction="row" sx={{ alignItems: 'flex-start', gap: 2.25, flexWrap: 'wrap' }}>
          <MemberPhotoAvatar
            contactId={contactId}
            name={name}
            photoUrl={data.photoUrl}
            tint={av}
            reloadToken={reloadToken}
            // The photo stays OWNER/ADMIN (DNKADM-0361, not in DNKADM-0375's scope), so a
            // STAFF session gets the initials avatar with no photo read and no camera.
            photoEnabled={fullAccess}
            onReload={onReload}
          />

          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography variant="h2" component="h1">
                <MemberNameText name={data.name} contactId={data.id ?? contactId} />
              </Typography>
              {tier && <Chip label={tier.label} sx={chipSx(tier)} />}
              {status && (
                <Chip
                  sx={chipSx(status)}
                  icon={
                    <Box
                      sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: status.dot, ml: 1 }}
                    />
                  }
                  label={status.label}
                />
              )}
              {paymentFailed && <Chip label={paymentFailed.label} sx={chipSx(paymentFailed)} />}
            </Stack>
            <Stack
              direction="row"
              sx={{ gap: 2.5, flexWrap: 'wrap', mt: 1.25, color: t.text2 }}
            >
              <Meta icon="mail" text={data.email ?? PLACEHOLDER} />
              <Meta
                icon="call"
                text={data.phone !== null ? formatPhoneForDisplay(data.phone) : PLACEHOLDER}
              />
              <Meta
                icon="calendar_today"
                text={
                  data.joinedAt !== null
                    ? `Member since ${formatJoined(data.joinedAt, calendarZone)}`
                    : PLACEHOLDER
                }
              />
              <Meta icon="tag" text={data.id ?? PLACEHOLDER} mono />
            </Stack>
          </Box>
          <Stack
            direction="row"
            sx={{ gap: 1.25, flexWrap: 'wrap', alignItems: 'center' }}
          >
            {/* The member's home location (DNKADM-0247) reads as header CONTEXT beside the
                overflow menu that changes it. An unset home stays an actionable defect —
                a warning-toned chip, never a neutral blank (DNKADM-0239) — and the chip
                now states the real consequence (DNKADM-0249): dink-api rejects a one-time
                sale before it charges without an effective home (`ensureHomeForPurchase`).
                The header stays compact, so the consequence rides on the chip rather than
                a paragraph; the picker carries the full explanation. Nothing here blocks a
                sale — the SERVER owns that rule and its single-active-location fallback
                may resolve the home anyway. */}
            <Stack sx={{ gap: 0.75 }}>
              <Stack
                direction="row"
                role="group"
                aria-label={
                  isHomeSet
                    ? `Home location: ${homeName(homeLocation)}`
                    : 'Home location: not set'
                }
                sx={{
                  alignItems: 'center',
                  gap: 0.75,
                  fontSize: 13,
                  color: t.text2,
                }}
              >
                <Icon
                  name={isHomeSet ? 'location_on' : 'location_off'}
                  sx={{ color: inkAlpha(theme, 0.4), fontSize: 17 }}
                />
                {isHomeSet ? (
                  homeName(homeLocation)
                ) : (
                  <Chip
                    size="small"
                    color="warning"
                    label="Not set"
                    title="Purchases can’t be completed for this member until a home location is set."
                  />
                )}
              </Stack>
              <Stack
                direction="row"
                role="group"
                aria-label={coachState === 'unassigned' ? coachText : `Coach: ${coachText}`}
                sx={{ alignItems: 'center', gap: 0.75, fontSize: 13, color: t.text2 }}
              >
                <Icon
                  name={coachState === 'orphaned' ? 'person_off' : 'sports'}
                  sx={{ color: inkAlpha(theme, 0.4), fontSize: 17 }}
                />
                {coachText}
              </Stack>
            </Stack>
            {/* Home location, coach and erasure are OWNER/ADMIN on the server, so for a
                STAFF session those ITEMS go — and the TRIGGER goes with them when nothing
                else is on offer, since an empty overflow menu is a worse affordance than
                none. "Resend verification email" (DNKADM-0444) is open to STAFF, so the
                trigger shows whenever at least one item is available. The home-location
                and coach lines above it stay, as header CONTEXT read off the member
                payload. */}
            {(fullAccess || showResend) && (
              <IconAction
                label="More member actions"
                aria-haspopup="menu"
                onClick={openActions}
                sx={{ border: `1px solid ${t.line}`, borderRadius: 1 }}
              >
                <Icon name="more_vert" />
              </IconAction>
            )}
            {(fullAccess || showResend) && (
              <Menu anchorEl={actionsAnchor} open={Boolean(actionsAnchor)} onClose={closeActions}>
                {/* Available to every operator — NOT admin-only (DNKADM-0247). */}
                {fullAccess && (
                  <MenuItem onClick={openHomeLocation}>
                    <Icon
                      name={isHomeSet ? 'swap_horiz' : 'add_location_alt'}
                      sx={{ fontSize: 18, mr: 1.25 }}
                    />
                    {isHomeSet ? 'Change home location' : 'Set home location'}
                  </MenuItem>
                )}
                {fullAccess && (
                  <MenuItem onClick={openCoach}>
                    <Icon
                      name={isCoachAssigned ? 'swap_horiz' : 'person_add'}
                      sx={{ fontSize: 18, mr: 1.25 }}
                    />
                    {isCoachAssigned ? 'Change assigned coach' : 'Set assigned coach'}
                  </MenuItem>
                )}
                {showResend && (
                  <MenuItem onClick={openResend}>
                    <Icon name="forward_to_inbox" sx={{ fontSize: 18, mr: 1.25 }} />
                    Resend verification email
                  </MenuItem>
                )}
                {fullAccess && isAdmin && (
                  <MenuItem onClick={openDelete} sx={{ color: 'error.main' }}>
                    <Icon name="delete_forever" sx={{ fontSize: 18, mr: 1.25 }} />
                    Delete member
                  </MenuItem>
                )}
              </Menu>
            )}
          </Stack>
        </Stack>
      </Paper>
      <Tabs
        value={tab}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        onChange={(_, v: number) => setTab(v)}
        aria-label="Member detail sections"
        sx={{
          borderBottom: `1px solid ${t.line}`,
          mt: 2.25,
          '& .MuiTabs-indicator': { bgcolor: t.brand },
          '& .Mui-selected': { color: `${t.brand} !important` },
        }}
      >
        {/* Explicit `value`s (DNKADM-0361): MUI numbers `Tab` children by position when
            they carry no value, so a new tab would silently repoint the panels. These are
            the positions MUI already assigned before Timeline and Payments joined. */}
        {/* DNKADM-0429: Timeline is the FIRST tab, with a NEW value (7) so no existing
            panel is renumbered. The default on open stays Membership/Credits (0,
            DNKADM-0235). */}
        <Tab value={7} label={TIMELINE_COPY.tabLabel} {...memberDetailTabA11yProps(7)} />
        <Tab value={0} label="Membership/Credits" {...memberDetailTabA11yProps(0)} />
        <Tab value={1} label="Payment methods" {...memberDetailTabA11yProps(1)} />
        {/* DNKADM-0391: Payments sits directly after Payment methods. Its value is NEW
            (6) — the existing values are not renumbered, so every other panel keeps its
            index and a11y pairing. */}
        <Tab value={6} label="Payments" {...memberDetailTabA11yProps(6)} />
        <Tab value={2} label="Reservations" {...memberDetailTabA11yProps(2)} />
        <Tab value={3} label="Profile" {...memberDetailTabA11yProps(3)} />
        <Tab value={4} label="Activity" {...memberDetailTabA11yProps(4)} />
        <Tab value={5} label="Guests" {...memberDetailTabA11yProps(5)} />
      </Tabs>
      <Box sx={{ pt: 2.5 }}>
        {tab === 7 && contactId && (
          <Box {...memberDetailPanelA11yProps(7)}>
            <MemberTimelineTab contactId={contactId} />
          </Box>
        )}
        {tab === 0 && (
          <Box {...memberDetailPanelA11yProps(0)}>
            <LedgerTab
              contactId={contactId}
              name={name}
              cardBuyer={cardBuyer}
              balances={balances}
              tier={membershipDisplay.tier}
              status={membershipDisplay.status}
              onReload={onReload}
              reloadToken={reloadToken}
              onAddCredits={openAdjust}
              canAdjustCredits={canAdjustCredits}
              canManage={fullAccess}
              onBuyMembership={openMembership}
              editOpen={membershipOpen}
              onEditOpenChange={setMembershipOpen}
            />
          </Box>
        )}
        {tab === 1 && contactId && (
          <Box {...memberDetailPanelA11yProps(1)}>
            <MemberPaymentMethodsTab contactId={contactId} buyer={cardBuyer} />
          </Box>
        )}
        {tab === 6 && contactId && (
          <Box {...memberDetailPanelA11yProps(6)}>
            <MemberPaymentsTab contactId={contactId} />
          </Box>
        )}
        {tab === 2 && contactId && (
          <Box {...memberDetailPanelA11yProps(2)}>
            <MemberReservationsPanel contactId={contactId} memberName={data.name} memberEmail={data.email} />
          </Box>
        )}
        {tab === 3 && contactId && (
          <Box {...memberDetailPanelA11yProps(3)}>
            {fullAccess ? (
              /* onSaved also reloads the DETAIL read (DNKADM-0304 U3): a saved
                 registry field that feeds the header (a writable name/phone)
                 must reflect there too, not only in the tab's own values. */
              <MemberProfileTab contactId={contactId} onSaved={onReload} />
            ) : (
              /* DNKADM-0361: a STAFF session reads the profile, it does not edit it.
                 The read-only panel renders the same profile from the member read
                 the header already loaded, with no fetch of its own. */
              <StaffMemberProfilePanel data={data} />
            )}
          </Box>
        )}
        {tab === 4 && contactId && (
          <Box {...memberDetailPanelA11yProps(4)}>
            <MemberActivitySection contactId={contactId} />
          </Box>
        )}
        {tab === 5 && contactId && (
          <Box {...memberDetailPanelA11yProps(5)}>
            <MemberGuestsTab contactId={contactId} />
          </Box>
        )}
      </Box>
      {/* Adjust credits follows the money seam (DNKAPI-0670: STAFF is refused, a free
          grant is a Comp behind its PIN); delete, home location and coach stay
          OWNER/ADMIN. None is mounted for a session that cannot open it. */}
      {canAdjustCredits && (
        <AdjustCreditsDialog
          contactId={contactId}
          open={adjustOpen}
          buckets={balances ?? []}
          onClose={() => setAdjustOpen(false)}
          onAdjusted={handleAdjusted}
        />
      )}
      {fullAccess && isAdmin && (
        <DeleteMemberDialog
          contactId={contactId}
          memberName={name}
          open={deleteOpen}
          onClose={() => setDeleteOpen(false)}
          onErased={handleErased}
        />
      )}
      {showResend && resendOpen && contactId && (
        <ResendVerificationDialog
          contactId={contactId}
          email={data.email}
          onClose={() => setResendOpen(false)}
          onDone={handleResendDone}
        />
      )}
      {fullAccess && homeChange.open && (
        <HomeLocationPickerDialog
          currentLocationId={homeLocation?.locationId ?? null}
          busy={homeChange.busy}
          error={homeChange.error}
          onClose={() => {
            if (!homeChange.busy) homeChange.setOpen(false);
          }}
          onPick={(option) => void homeChange.submit(option)}
        />
      )}
      {fullAccess && coachChange.open && (
        <CoachPickerDialog
          key={coachChange.pickerVersion}
          currentStaffId={coach.staffId}
          assigned={isCoachAssigned}
          busy={coachChange.busy}
          error={coachChange.error}
          canRetry={coachChange.canRetry}
          onClose={() => {
            if (!coachChange.busy) coachChange.setOpen(false);
          }}
          onAssign={(staffId) => void coachChange.assign(staffId)}
          onRemove={() => void coachChange.clear()}
          onRetry={coachChange.retry}
        />
      )}
    </>
  );
}

/** A header meta item: a muted glyph + text (mono for ids/numbers). */
function Meta({ icon, text, mono }: { icon: string; text: string; mono?: boolean }) {
  const theme = useTheme();
  return (
    <Stack
      direction="row"
      sx={{ alignItems: 'center', gap: 0.75, fontSize: 13, fontFamily: mono ? MONO : undefined }}
    >
      <Icon name={icon} sx={{ color: inkAlpha(theme, 0.4), fontSize: 17 }} />
      {text}
    </Stack>
  );
}
